import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Stack,
  Box,
  CircularProgress,
} from "@mui/material";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import { useData } from "../hooks/useData";
import { useFirebase } from "../hooks/useFirebase";
import { collection, addDoc } from "firebase/firestore";
import { firestore } from "../firebase";

const SaveToMyAccountDialog = ({ open, onClose, canvas, onSaveSuccess }) => {
  const { projectName, setProjectName, user } = useData();
  const { saveDesign } = useFirebase();
  const [fileName, setFileName] = useState(projectName || "");
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // Track saving state
  const [fileNameError, setFileNameError] = useState("");
  const [dialogPosition, setDialogPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const dialogRef = useRef(null);

  // File name validation
  const maxFileNameLength = 50;
  const fileNameRegex = /^[a-zA-Z0-9\s\-_]+$/;
  const validateFileName = (name) => {
    if (name.trim() === "") {
      return "File name is required.";
    }
    if (name.length > maxFileNameLength) {
      return `File name must be ${maxFileNameLength} characters or less.`;
    }
    if (!fileNameRegex.test(name)) {
      return "File name can only contain letters, numbers, spaces, hyphens, and underscores.";
    }
    return "";
  };

  const isSaveDisabled = fileName.trim() === "" || !!fileNameError || isSaving;

  const handleFileNameChange = (e) => {
    const newName = e.target.value;
    setFileName(newName);
    setFileNameError(validateFileName(newName));
  };

  const handleSave = async () => {
    if (!canvas) return;
    setIsSaving(true); // Set saving state
    try {
      // Save canvas as JSON and PNG to Firebase Storage
      const designData = await saveDesign();
      if (!designData) throw new Error("Failed to save design to storage");

      // Save metadata to Firestore
      const effectiveUserId = user?.uid || "guest";
      await addDoc(collection(firestore, "customerSavedFiles"), {
        customerId: effectiveUserId,
        fileName: fileName,
        canvasObj: JSON.stringify(canvas.toJSON()),
        jsonPath: designData.jsonPath,
        pngUrl: designData.pngUrl,
        createdAt: new Date(),
      });

      // Update projectName in context
      setProjectName(fileName);
      setIsSaved(true);
      onSaveSuccess?.();
    } catch (e) {
      console.error("Error saving to my account:", e);
      // Snackbar error is handled by useFirebase's saveDesign
    } finally {
      setIsSaving(false); // Reset saving state
    }
  };

  const handleClose = () => {
    if (isSaving) return; // Prevent closing while saving
    setFileName(projectName || "");
    setFileNameError("");
    setIsSaved(false);
    setDialogPosition({ x: 0, y: 0 });
    onClose();
  };

  // Draggable functionality
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - dialogPosition.x,
      y: e.clientY - dialogPosition.y,
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setDialogPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          minWidth: 400,
          transform: `translate(${dialogPosition.x}px, ${dialogPosition.y}px)`,
        },
      }}
      aria-labelledby="dialog-title"
      maxWidth="sm"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <DialogTitle
        id="dialog-title"
        sx={{
          bgcolor: "primary.main",
          color: "white",
          fontSize: "1.25rem",
          cursor: "move",
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          pb: 2,
        }}
        onMouseDown={handleMouseDown}
      >
        <DragIndicatorOutlinedIcon sx={{ fontSize: 20, mr: 1 }} />
        Save to My Account
      </DialogTitle>
      <DialogContent sx={{ mt: 2, pt: 2, pb: 3 }}>
        {!isSaved ? (
          <Stack direction="column" spacing={3}>
            <TextField
              id="fileName"
              label="File Name"
              value={fileName}
              onChange={handleFileNameChange}
              variant="outlined"
              size="medium"
              required
              fullWidth
              sx={{
                maxWidth: 350,
                mt: 2,
                pt: 1,
                "& .MuiInputLabel-root": {
                  fontSize: "1rem",
                  pt: 1,
                },
              }}
              error={!!fileNameError}
              helperText={fileNameError || "Enter a name for your design"}
              disabled={isSaving}
            />
            <DialogActions sx={{ justifyContent: "flex-end", px: 0 }}>
              <Button
                onClick={handleClose}
                color="secondary"
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaveDisabled}
                variant="contained"
                color="primary"
                startIcon={isSaving ? <CircularProgress size={20} /> : null}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Stack>
        ) : (
          <Stack direction="column" spacing={2} alignItems="center">
            <Typography variant="h6" color="success.main">
              Success
            </Typography>
            <Typography variant="body1">
              Your design has been saved to your account.
            </Typography>
            <Button
              onClick={handleClose}
              variant="contained"
              color="success"
              sx={{ mt: 1 }}
            >
              Close
            </Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SaveToMyAccountDialog;
